import { WireTickCache, type WireEncoding } from './wire-compact.js';
import type {
  AttackDelta, AttackSnapshot, MonsterDelta, MonsterSnapshot, NetObjectId, ObjectSnapshot, ObjectUpdate,
  PlayerDelta, PlayerSnapshot, PresentationEvent, ReplicationDelta, ReplicationSnapshot,
} from './protocol.js';
import { SERVER_BASELINE_RETENTION_TICKS, applyObjectUpdate } from './protocol.js';
import { canonicalStatusViews, compareCodeUnits, sameStatusSummary, sameStatusViews } from './combat-projection.js';

function sameObject(a: ObjectSnapshot | undefined, b: ObjectSnapshot): boolean {
  if (!a || a.kind !== b.kind || a.netId !== b.netId) return false;
  if (b.kind === 'attack') return a.kind === 'attack'
    && a.ownerNetId === b.ownerNetId && a.skillId === b.skillId && a.x === b.x && a.y === b.y
    && a.vx === b.vx && a.vy === b.vy && a.radiusM === b.radiusM && a.replication === b.replication
    && a.visualProfileId === b.visualProfileId && a.stageId === b.stageId && a.presentationKind === b.presentationKind
    && a.orbitRadiusM === b.orbitRadiusM && sameJson(a.orbitCenter, b.orbitCenter);
  if (b.kind === 'monster') return a.kind === 'monster'
    && a.x === b.x && a.y === b.y && a.hp === b.hp && a.maxHp === b.maxHp && a.level === b.level && a.alive === b.alive
    && a.shield === b.shield && a.vx === b.vx && a.vy === b.vy && sameStatusViews(a.statuses, b.statuses)
    && sameStatusSummary(a.statusSummary, b.statusSummary) && (a.telegraph === b.telegraph || sameJson(a.telegraph, b.telegraph)) && a.archetypeId === b.archetypeId && a.grade === b.grade;
  return a.kind === 'player'
    && a.x === b.x && a.y === b.y && a.hp === b.hp && a.maxHp === b.maxHp && a.mana === b.mana
    && a.maxMana === b.maxMana && a.moveSpeedMps === b.moveSpeedMps && a.movementAllowed === b.movementAllowed
    && sameJson(a.facing, b.facing) && a.weaponId === b.weaponId && sameJson(a.basicAttack, b.basicAttack)
    && sameJson(a.inputSlotMap, b.inputSlotMap)
    && a.connected === b.connected && a.shield === b.shield && a.down === b.down
    && sameNumbers(a.cooldownTicks, b.cooldownTicks) && sameNumbers(a.cooldownReadyTicks, b.cooldownReadyTicks) && sameStatusViews(a.statuses, b.statuses);
}

/** Value as `JSON.stringify` writes it inside an array: a value with no JSON form becomes `null`. */
function jsonArrayElement(value: unknown): unknown {
  return value === undefined || typeof value === 'function' || typeof value === 'symbol' ? null : value;
}

function omittedFromJsonObject(value: unknown): boolean {
  return value === undefined || typeof value === 'function' || typeof value === 'symbol';
}

/**
 * Exactly `JSON.stringify(a) === JSON.stringify(b)` for plain replicated data
 * (no `toJSON`, no bigint), without serializing: key order counts, keys whose
 * value has no JSON form are skipped, and a non-finite number equals `null`.
 */
export function sameJson(a: unknown, b: unknown): boolean {
  if (typeof a === 'number' && !Number.isFinite(a)) a = null;
  if (typeof b === 'number' && !Number.isFinite(b)) b = null;
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const right = b as readonly unknown[];
    if (a.length !== right.length) return false;
    for (let index = 0; index < a.length; index++) if (!sameJson(jsonArrayElement(a[index]), jsonArrayElement(right[index]))) return false;
    return true;
  }
  const left = a as Record<string, unknown>; const right = b as Record<string, unknown>;
  const leftKeys = Object.keys(left); const rightKeys = Object.keys(right);
  let j = 0;
  for (const key of leftKeys) {
    if (omittedFromJsonObject(left[key])) continue;
    while (j < rightKeys.length && omittedFromJsonObject(right[rightKeys[j]!])) j++;
    if (j >= rightKeys.length || rightKeys[j] !== key || !sameJson(left[key], right[key])) return false;
    j++;
  }
  while (j < rightKeys.length && omittedFromJsonObject(right[rightKeys[j]!])) j++;
  return j === rightKeys.length;
}

function importantChanged(a: ObjectSnapshot | undefined, b: ObjectSnapshot): boolean {
  if (!a || a.kind !== b.kind) return true;
  if (b.kind === 'attack') return false;
  if (b.kind === 'monster') return a.kind !== 'monster' || a.level !== b.level || a.hp !== b.hp || a.alive !== b.alive
    || a.shield !== b.shield || !sameStatusViews(a.statuses, b.statuses) || !sameStatusSummary(a.statusSummary, b.statusSummary) || (a.telegraph !== b.telegraph && !sameJson(a.telegraph, b.telegraph));
  return a.kind !== 'player' || a.hp !== b.hp || a.mana !== b.mana || a.connected !== b.connected
    || a.maxMana !== b.maxMana || a.moveSpeedMps !== b.moveSpeedMps || a.movementAllowed !== b.movementAllowed || !sameJson(a.facing, b.facing)
    || a.weaponId !== b.weaponId || !sameJson(a.basicAttack, b.basicAttack) || !sameJson(a.inputSlotMap, b.inputSlotMap)
    || a.down !== b.down || a.shield !== b.shield || !sameNumbers(a.cooldownTicks, b.cooldownTicks) || !sameNumbers(a.cooldownReadyTicks, b.cooldownReadyTicks)
    || !sameStatusViews(a.statuses, b.statuses);
}

function sameNumbers(a: readonly number[] | undefined, b: readonly number[] | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * Field-level delta of a player against its ACK baseline. Only changed fields
 * are emitted; the receiver merges onto the baseline it already holds. The full
 * partition decision (`importantChanged` / `sameObject`) is made on full objects
 * before this runs, so a delta may legitimately carry both important and
 * positional fields when they changed on the same tick.
 */
function playerDelta(base: PlayerSnapshot, next: PlayerSnapshot): PlayerDelta {
  const delta: PlayerDelta = { kind: 'player', netId: next.netId };
  if (next.x !== base.x) delta.x = next.x;
  if (next.y !== base.y) delta.y = next.y;
  if (next.hp !== base.hp) delta.hp = next.hp;
  if (next.maxHp !== base.maxHp) delta.maxHp = next.maxHp;
  if (next.mana !== base.mana) delta.mana = next.mana;
  if (next.maxMana !== base.maxMana) delta.maxMana = next.maxMana;
  if (next.moveSpeedMps !== base.moveSpeedMps) delta.moveSpeedMps = next.moveSpeedMps;
  if (next.movementAllowed !== base.movementAllowed) delta.movementAllowed = next.movementAllowed;
  if (!sameJson(base.facing, next.facing)) delta.facing = next.facing;
  if (next.weaponId !== base.weaponId) delta.weaponId = next.weaponId;
  if (!sameJson(base.basicAttack, next.basicAttack)) delta.basicAttack = next.basicAttack;
  if (!sameJson(base.inputSlotMap, next.inputSlotMap)) delta.inputSlotMap = next.inputSlotMap;
  if (!sameNumbers(base.cooldownTicks, next.cooldownTicks)) delta.cooldownTicks = next.cooldownTicks;
  if (!sameNumbers(base.cooldownReadyTicks, next.cooldownReadyTicks)) delta.cooldownReadyTicks = next.cooldownReadyTicks;
  if (next.connected !== base.connected) delta.connected = next.connected;
  if (!sameStatusViews(base.statuses, next.statuses)) delta.statuses = next.statuses;
  if (next.shield !== base.shield) delta.shield = next.shield;
  if (next.down !== base.down) delta.down = next.down;
  return delta;
}

function monsterDelta(base: MonsterSnapshot, next: MonsterSnapshot): MonsterDelta {
  const delta: MonsterDelta = { kind: 'monster', netId: next.netId };
  if (next.level !== base.level) delta.level = next.level;
  if (next.x !== base.x) delta.x = next.x;
  if (next.y !== base.y) delta.y = next.y;
  if (next.hp !== base.hp) delta.hp = next.hp;
  if (next.maxHp !== base.maxHp) delta.maxHp = next.maxHp;
  if (next.alive !== base.alive) delta.alive = next.alive;
  if (!sameStatusViews(base.statuses, next.statuses)) delta.statuses = next.statuses;
  if (!sameStatusSummary(base.statusSummary, next.statusSummary)) delta.statusSummary = next.statusSummary ?? null;
  if (next.shield !== base.shield) delta.shield = next.shield;
  if (next.vx !== base.vx) delta.vx = next.vx;
  if (next.vy !== base.vy) delta.vy = next.vy;
  if (!sameJson(base.telegraph, next.telegraph)) delta.telegraph = next.telegraph;
  if (next.archetypeId !== base.archetypeId) delta.archetypeId = next.archetypeId;
  if (next.grade !== base.grade) delta.grade = next.grade;
  return delta;
}

/**
 * `replication` is retained on every attack delta because the budget scheduler
 * classifies cadence from it each tick without baseline access; the other
 * immutable identity fields only travel in the spawn full state.
 */
function attackDelta(base: AttackSnapshot, next: AttackSnapshot): AttackDelta {
  const delta: AttackDelta = { kind: 'attack', netId: next.netId, replication: next.replication };
  if (next.ownerNetId !== base.ownerNetId) delta.ownerNetId = next.ownerNetId;
  if (next.skillId !== base.skillId) delta.skillId = next.skillId;
  if (next.x !== base.x) delta.x = next.x;
  if (next.y !== base.y) delta.y = next.y;
  if (next.vx !== base.vx) delta.vx = next.vx;
  if (next.vy !== base.vy) delta.vy = next.vy;
  if (next.radiusM !== base.radiusM) delta.radiusM = next.radiusM;
  if (next.visualProfileId !== base.visualProfileId) delta.visualProfileId = next.visualProfileId;
  if (next.stageId !== base.stageId) delta.stageId = next.stageId;
  if (next.presentationKind !== base.presentationKind) delta.presentationKind = next.presentationKind;
  if (next.orbitRadiusM !== base.orbitRadiusM && next.orbitRadiusM !== undefined) delta.orbitRadiusM = next.orbitRadiusM;
  if (!sameJson(next.orbitCenter, base.orbitCenter) && next.orbitCenter !== undefined) delta.orbitCenter = next.orbitCenter;
  return delta;
}

/** Field-level delta against the ACK baseline for every replicated kind. */
function toUpdate(object: ObjectSnapshot, baseline: ObjectSnapshot | undefined): ObjectUpdate {
  if (!baseline || baseline.kind !== object.kind) return object;
  if (object.kind === 'player') return playerDelta(baseline as PlayerSnapshot, object);
  if (object.kind === 'monster') return monsterDelta(baseline as MonsterSnapshot, object);
  return attackDelta(baseline as AttackSnapshot, object);
}

/** A status-free object is already canonical, so it stays the one object every client shares. */
function canonicalObject(object: ObjectSnapshot): ObjectSnapshot {
  if (object.kind === 'attack' || object.statuses.length === 0) return object;
  return { ...object, statuses: canonicalStatusViews(object.statuses) };
}

function compareNetIds(a: { netId: NetObjectId }, b: { netId: NetObjectId }): number {
  return compareCodeUnits(a.netId, b.netId);
}

interface LifecycleRecord<T> { tick: number; value: T; }

/**
 * A delivered baseline: what the client holds for one server tick. Objects are
 * shared by reference with the snapshot and delta they came from and with
 * other baselines; only an object that changed is a new object.
 */
interface DeliveredBaseline { serverTick: number; objects: ReadonlyMap<NetObjectId, ObjectSnapshot>; }

/**
 * Server-side per-client ACK baseline and reliable lifecycle state.
 *
 * Ownership: every object handed to `build()` (with its nested values) and
 * every object of a delta passed to `recordDelivered()` is immutable from then
 * on, so baselines keep references instead of copies. The authoritative
 * projection builds fresh objects every tick; an in-process consumer that needs
 * to mutate received state clones it first (`PredictedClient` does).
 */
export class ReplicationStream {
  private readonly baselines = new Map<number, DeliveredBaseline>();
  private previous = new Map<NetObjectId, ObjectSnapshot>();
  private readonly reliableSpawns = new Map<NetObjectId, LifecycleRecord<ObjectSnapshot>>();
  private readonly reliableDespawns = new Map<NetObjectId, LifecycleRecord<NetObjectId>>();
  private acknowledgedTick: number | null = null;

  acknowledge(serverTick: number): boolean {
    // A stale ACK — for a tick this stream already pruned because a newer ACK
    // moved the anchor past it — is rejected here, not treated as an error;
    // the next (non-stale) ACK recovers normally. See protocol.ts.
    if (!Number.isSafeInteger(serverTick) || !this.baselines.has(serverTick)) return false;
    if (this.acknowledgedTick !== null && serverTick < this.acknowledgedTick) return false;
    this.acknowledgedTick = serverTick;
    for (const [id, record] of this.reliableSpawns) if (record.tick <= serverTick) this.reliableSpawns.delete(id);
    for (const [id, record] of this.reliableDespawns) if (record.tick <= serverTick) this.reliableDespawns.delete(id);
    this.pruneBaselines(serverTick);
    return true;
  }

  /**
   * Bounds retained baselines to the recent window plus the current ACK
   * anchor, which is pinned regardless of age — it is exactly the baseline
   * `build()` is still naming while unacknowledged, so evicting it on a
   * window basis alone reproduces NET-A2/NET-A3. Called both when a fresh
   * baseline is recorded (bounds a never-ACKing client) and when the anchor
   * itself advances (releases everything the new ACK made obsolete).
   */
  private pruneBaselines(latestTick: number): void {
    for (const tick of this.baselines.keys()) {
      if (tick === this.acknowledgedTick) continue;
      if (tick < latestTick - SERVER_BASELINE_RETENTION_TICKS) this.baselines.delete(tick);
    }
  }

  /** Diagnostic: retained baseline count, bounded by the recent window plus at most one ACK-anchor pin. */
  retainedBaselineCount(): number { return this.baselines.size; }

  build(snapshot: ReplicationSnapshot, events: readonly PresentationEvent[] = []): ReplicationDelta {
    const objects: ObjectSnapshot[] = new Array(snapshot.objects.length);
    const current = new Map<NetObjectId, ObjectSnapshot>();
    for (let index = 0; index < objects.length; index++) {
      const object = canonicalObject(snapshot.objects[index]!);
      objects[index] = object;
      current.set(object.netId, object);
    }
    for (const [id, object] of current) {
      if (!this.previous.has(id)) { this.reliableSpawns.set(id, { tick: snapshot.serverTick, value: object }); this.reliableDespawns.delete(id); }
      // Lifecycle remains reliable, but its payload must reflect the newest
      // authoritative combat state while the enter packet is unacknowledged.
      else if (this.reliableSpawns.has(id)) {
        const record = this.reliableSpawns.get(id)!;
        this.reliableSpawns.set(id, { tick: record.tick, value: object });
      }
    }
    for (const id of this.previous.keys()) {
      if (!current.has(id)) { this.reliableDespawns.set(id, { tick: snapshot.serverTick, value: id }); this.reliableSpawns.delete(id); }
    }
    this.previous = current;
    const baseline = this.acknowledgedTick === null ? undefined : this.baselines.get(this.acknowledgedTick);
    const baselineObjects: ReadonlyMap<NetObjectId, ObjectSnapshot> = baseline?.objects ?? new Map();
    // One pass partitions what changed against the baseline (net ids are unique within a snapshot): important or disposable.
    const important: ObjectSnapshot[] = [];
    const disposable: ObjectSnapshot[] = [];
    for (const object of objects) {
      if (this.reliableSpawns.has(object.netId)) continue;
      const base = baselineObjects.get(object.netId);
      if (sameObject(base, object)) continue;
      (importantChanged(base, object) ? important : disposable).push(object);
    }
    return {
      roomId: snapshot.roomId, serverTick: snapshot.serverTick, baselineTick: baseline?.serverTick ?? null,
      lastProcessedInputSequence: snapshot.lastProcessedInputSequence,
      spawns: [...this.reliableSpawns.values()].map((record) => record.value).sort(compareNetIds),
      importantUpdates: important.sort(compareNetIds).map((object) => toUpdate(object, baselineObjects.get(object.netId))),
      updates: disposable.sort(compareNetIds).map((object) => toUpdate(object, baselineObjects.get(object.netId))),
      despawns: [...this.reliableDespawns.keys()].sort(), events: [...events],
    };
  }

  /** Records exactly what survived cadence/budget scheduling, never unsent authoritative state. */
  recordDelivered(delta: ReplicationDelta): void {
    const base = delta.baselineTick === null ? undefined : this.baselines.get(delta.baselineTick);
    const objects = new Map(base?.objects);
    for (const id of delta.despawns) objects.delete(id);
    for (const object of delta.spawns) objects.set(object.netId, object);
    for (const update of delta.importantUpdates) objects.set(update.netId, applyObjectUpdate(objects.get(update.netId), update));
    for (const update of delta.updates) objects.set(update.netId, applyObjectUpdate(objects.get(update.netId), update));
    this.baselines.set(delta.serverTick, { serverTick: delta.serverTick, objects });
    this.pruneBaselines(delta.serverTick);
  }
}

export interface ReplicationBudgetConfig {
  byteBudget: number;
  monsterCadenceTicks: number;
  correctedAttackCadenceTicks: number;
  /**
   * Distance boundary between the transform-near and transform-far cadence
   * categories, measured from the viewer. Defaults to Infinity so every remote
   * transform is "near" and the default publication cadence is unchanged from
   * pure simulation rate; a finite value opts into far-transform down-sampling.
   */
  nearRadiusM: number;
  /** Publication interval (ticks) for near-viewer remote player transforms. */
  playerNearCadenceTicks: number;
  /** Publication interval (ticks) for far remote player transforms. */
  playerFarCadenceTicks: number;
}
export const DEFAULT_REPLICATION_BUDGET: ReplicationBudgetConfig = {
  byteBudget: 12_000, monsterCadenceTicks: 3, correctedAttackCadenceTicks: 4,
  nearRadiusM: Infinity, playerNearCadenceTicks: 1, playerFarCadenceTicks: 1,
};

/**
 * How far behind the newest snapshot a client draws monsters: one more tick than the server's monster
 * transform cadence, so two buffered samples always bracket the drawn tick and a monster is never held still.
 */
export const MONSTER_INTERPOLATION_DELAY_TICKS = DEFAULT_REPLICATION_BUDGET.monsterCadenceTicks + 1;

/** Authoritative positions the scheduler needs to bucket transforms by distance. */
export interface ScheduleContext {
  encoding?: WireEncoding;
  viewer?: { x: number; y: number };
  positions?: ReadonlyMap<NetObjectId, { x: number; y: number }>;
}

/** Stable per-object phase so equal-cadence transforms spread across ticks. */
function cadencePhase(netId: NetObjectId, interval: number): number {
  if (interval <= 1) return 0;
  let hash = 0;
  for (let index = 0; index < netId.length; index++) hash = (hash * 31 + netId.charCodeAt(index)) | 0;
  return ((hash % interval) + interval) % interval;
}

function updatePriority(object: ObjectUpdate, localId: NetObjectId): number {
  if (object.kind === 'player') return object.netId === localId ? 1000 : 900;
  if (object.kind === 'attack') return object.replication === 'STATEFUL' ? 800 : object.replication === 'CORRECTED' ? 600 : 0;
  return 500;
}

/** Selected-encoding UTF-8 JSON bytes for contacts, including nested regions and array punctuation. */
export const WEAPON_CONTACTS_BYTE_CAP = 2_048;

function capPresentationContacts(event: PresentationEvent, wire: WireTickCache): PresentationEvent {
  if (event.type !== 'weapon_contact') return event;
  let bytes = 2;
  let count = 0;
  for (const contact of event.contacts) {
    const cost = wire.row(contact).bytes + (count === 0 ? 0 : 1);
    if (bytes + cost > WEAPON_CONTACTS_BYTE_CAP) break;
    bytes += cost;
    count++;
  }
  return count === event.contacts.length ? event : structuredClone({ ...event, contacts: event.contacts.slice(0, count) });
}

/** Applies cadence and a byte budget only to disposable state/events, never lifecycle. */
export class ReplicationBudgetScheduler {
  readonly config: ReplicationBudgetConfig;
  private readonly wireCaches = new Map<WireEncoding, WireTickCache>();
  private sharedLengthsTick: number | null = null;
  private readonly presentationEvents = new Map<PresentationEvent, Map<WireEncoding, PresentationEvent>>();
  constructor(config: Partial<ReplicationBudgetConfig> = {}) { this.config = { ...DEFAULT_REPLICATION_BUDGET, ...config }; }

  schedule(delta: ReplicationDelta, localId: NetObjectId, context: ScheduleContext = {}): ReplicationDelta {
    if (delta.serverTick !== this.sharedLengthsTick) {
      this.wireCaches.clear(); this.presentationEvents.clear(); this.sharedLengthsTick = delta.serverTick;
    }
    const encoding = context.encoding ?? 'plain';
    let wire = this.wireCaches.get(encoding);
    if (!wire) { wire = new WireTickCache(encoding); this.wireCaches.set(encoding, wire); }
    const projectedEvents = delta.events.map((event) => {
      let projections = this.presentationEvents.get(event);
      if (!projections) { projections = new Map(); this.presentationEvents.set(event, projections); }
      let projected = projections.get(encoding);
      if (!projected) { projected = capPresentationContacts(event, wire); projections.set(encoding, projected); }
      return projected;
    });
    // Semantic lifecycle and local combat feedback survive budget pressure.
    const mandatory = projectedEvents.filter((event) =>
      event.type === 'blast_detonation'
      || event.type === 'basic_attack_start' || event.type === 'basic_attack_strike' || event.type === 'basic_attack_cancel'
      || ((event.type === 'damage' || event.type === 'death') && event.targetId === localId)
      || ((event.type === 'skill_displacement' || event.type === 'cast' || event.type === 'weapon_contact' || event.type === 'skill_impact') && event.sourceId === localId));
    const mandatorySourceIds = new Set(mandatory.flatMap((event) => {
      if (event.type === 'damage' || event.type === 'death' || event.type === 'blast_detonation' || event.type === 'skill_displacement') {
        return event.sourceId === undefined ? [] : [event.sourceId];
      }
      return [];
    }));
    // The local transform carries input acknowledgement. Due monster transforms carry the interpolation
    // deadline: dropping one would leave the client's delayed tick without a bracketing sample.
    // Like lifecycle traffic, these may overrun the soft byte budget after higher-priority transforms.
    const updates: ObjectUpdate[] = delta.updates.filter((object) => object.netId === localId || mandatorySourceIds.has(object.netId));
    const events: PresentationEvent[] = [...mandatory];
    // All fixed traffic is measured together: envelope, frame-local templates,
    // array delimiters and mandatory overruns are part of the same payload.
    const extra = (values: readonly object[]) => values.reduce((sum, value) => sum + wire.row(value).bytes, Math.max(0, values.length - 1));
    const spawn = wire.spawn(delta.spawns);
    const spawnBytes = spawn.rows.bytes - 2 + (spawn.templates ? 7 + spawn.templates.bytes : 0);
    const header = wire.header(delta);
    const despawns = wire.despawns(delta.despawns);
    const fixed = header.bytes
      + spawnBytes + despawns.bytes - 2 + extra(delta.importantUpdates) + extra(updates) + extra(events);
    let remaining = Math.max(0, this.config.byteBudget) - fixed;
    const acceptedIds = new Set(updates.map((object) => object.netId));
    const candidates = delta.updates.filter((object) => this.due(object, delta.serverTick, localId, context))
      .sort((a, b) => updatePriority(b, localId) - updatePriority(a, localId) || compareCodeUnits(a.netId, b.netId));
    for (const object of candidates) {
      if (acceptedIds.has(object.netId)) continue;
      const cost = wire.row(object).bytes + (updates.length > 0 ? 1 : 0); if (cost > remaining && object.kind !== 'monster') continue;
      updates.push(object); acceptedIds.add(object.netId); remaining -= cost;
    }
    const mandatoryEvents = new Set<PresentationEvent>(mandatory);
    for (const event of projectedEvents) {
      if (mandatoryEvents.has(event)) continue;
      const cost = wire.row(event).bytes + (events.length > 0 ? 1 : 0); if (cost > remaining) continue;
      events.push(event); remaining -= cost;
    }
    const included = new Set(events);
    const scheduled = { ...delta, updates, events: projectedEvents.filter((event) => included.has(event)) };
    wire.retain(scheduled, spawn, despawns, header);
    return scheduled;
  }

  /**
   * Cadence gate for disposable transform updates only. Critical/state-change
   * facts travel in `importantUpdates` and never reach this path, so a cadence
   * skip can never drop hp, status, down, spawn, or despawn. The local player's
   * own transform is always published (critical, full sim rate).
   */
  private due(object: ObjectUpdate, tick: number, localId: NetObjectId, context: ScheduleContext): boolean {
    if (object.kind === 'monster') return tick % Math.max(1, this.config.monsterCadenceTicks) === 0;
    if (object.kind === 'attack') {
      if (object.replication === 'DETERMINISTIC') return false;
      // A corrected attack is extrapolated along its velocity between corrections, which only holds while
      // that velocity stands: one whose velocity moved off the baseline's (an orb, a curving shot) is due now.
      if (object.replication === 'CORRECTED') return object.vx !== undefined || object.vy !== undefined || tick % Math.max(1, this.config.correctedAttackCadenceTicks) === 0;
      return true;
    }
    if (object.netId === localId) return true; // self transform is critical
    const interval = this.playerCadence(object.netId, context);
    return this.phased(object.netId, tick, interval);
  }

  /** Near/far distance bucket for a remote player transform. */
  private playerCadence(netId: NetObjectId, context: ScheduleContext): number {
    const viewer = context.viewer; const position = context.positions?.get(netId);
    if (!viewer || !position || !Number.isFinite(this.config.nearRadiusM)) return Math.max(1, this.config.playerNearCadenceTicks);
    const near = Math.hypot(position.x - viewer.x, position.y - viewer.y) <= this.config.nearRadiusM;
    return Math.max(1, near ? this.config.playerNearCadenceTicks : this.config.playerFarCadenceTicks);
  }

  private phased(netId: NetObjectId, tick: number, intervalTicks: number): boolean {
    const interval = Math.max(1, intervalTicks);
    return ((tick - cadencePhase(netId, interval)) % interval + interval) % interval === 0;
  }
}
